<div class="container-fluid complyon-page privacy-page px-3 px-md-4 px-xl-4 py-3">
  <div class="complyon-shell privacy-shell mx-auto">
    <section class="mb-3">
      <h1 class="complyon-title privacy-title mb-2 text-l">Data privacy</h1>
      <p class="complyon-subtitle privacy-subtitle mb-0 text-s">POPIA register, DSARs, breach workflow, data sharing, retention and privacy impact assessments.</p>
    </section>

    <section class="d-flex align-items-start justify-content-between gap-3 mb-3 privacy-toolbar">
      <div class="d-inline-flex flex-wrap align-items-center gap-1 rounded-4 p-1 border border-light-subtle bg-white" role="tablist" aria-label="Data privacy tabs">
        <button
          v-for="tab in privacy_tabs"
          :key="tab.id"
          type="button"
          class="btn doa-tab-btn fw-semibold"
          :class="{ active: active_tab === tab.id }"
          @click="set_tab(tab.id)"
        >
          <span>{{ tab.label }}</span>
        </button>
      </div>

      <button type="button" class="btn btn-link text-decoration-none config-navbar-secondary-action d-inline-flex align-items-center gap-2">
        <i class="bi bi-arrow-clockwise"></i>
        <span>Reset sample</span>
      </button>
    </section>

    <div v-if="active_tab === 'dashboard'">
      <section class="complyon-panel rounded-4 p-3 p-md-4 mb-3">
        <div class="d-flex flex-column flex-md-row align-items-md-center gap-3">
          <div class="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0" :style="{ width: '84px', height: '84px', background: `conic-gradient(${dashboard_hero.border} 0 63%, #e7edf4 63% 100%)`, padding: '8px' }">
            <div class="d-flex flex-column align-items-center justify-content-center rounded-circle bg-white w-100 h-100">
              <div class="fw-bold text-dark" style="font-size: 2rem; line-height: 1;">{{ dashboard_hero.score }}</div>
            </div>
          </div>

          <div class="flex-grow-1">
            <div class="complyon-section-title text-s mb-1">POPIA compliance dashboard</div>
            <div class="complyon-section-subtitle text-xs mb-2">Overall readiness across processing, requests, sharing, assessments, retention, transfers and incidents.</div>
            <div class="d-flex flex-wrap gap-2">
              <span v-for="chip in dashboard_hero.chips" :key="chip.label" class="badge rounded-pill complyon-badge text-xs" :class="chip.class">{{ chip.label }}</span>
            </div>
          </div>
        </div>
      </section>

      <section v-for="(row, row_index) in dashboard_card_rows" :key="row_index" class="row g-3 mb-3">
        <div v-for="card in row" :key="card.title" class="col-12 col-md-6 col-xl-3">
          <button
            type="button"
            class="complyon-card rounded-4 p-3 h-100 w-100 text-start border-0 bg-white"
            @click="set_tab(card.tab_id)"
            :aria-label="`Open ${card.title}`"
          >
            <div class="d-flex align-items-start justify-content-between gap-2 mb-3">
              <div class="d-inline-flex align-items-center justify-content-center rounded-3 flex-shrink-0" :style="{ width: '2rem', height: '2rem', backgroundColor: card.bg, color: card.color }">
                <i :class="`bi ${card.icon}`"></i>
              </div>
            </div>
            <div class="fw-semibold text-s mb-1">{{ card.title }}</div>
            <div class="fw-semibold text-dark" style="font-size: 1.9rem; line-height: 1;">{{ card.value }}</div>
            <div class="text-xs mt-3 text-body-secondary">{{ card.note }}</div>
          </button>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'pi-inventory'">
      <section class="complyon-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
          <div>
            <div class="complyon-section-title text-s">Personal information inventory</div>
            <div class="complyon-section-subtitle text-xs">The catalogue of personal information held - what it is, who it relates to, where it lives and which processing activity uses it.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn" @click="open_pi_inventory_modal()">
            <i class="bi bi-plus-lg"></i>
            <span>New PI element</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table privacy-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Ref</th>
                <th class="text-xs">PI element</th>
                <th class="text-xs">Data subject</th>
                <th class="text-xs">Category</th>
                <th class="text-xs">System</th>
                <th class="text-xs">Sensitivity</th>
                <th class="text-xs">Status</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in pi_inventory_rows"
                :key="row.ref"
                class="privacy-pi-row"
                tabindex="0"
                role="button"
                @click="open_pi_inventory_drawer(row)"
                @keydown.enter.prevent="open_pi_inventory_drawer(row)"
                @keydown.space.prevent="open_pi_inventory_drawer(row)"
              >
                <td class="ps-3 fw-semibold text-success">{{ row.ref }}</td>
                <td class="fw-semibold privacy-name">{{ row.item }}</td>
                <td>{{ row.subject }}</td>
                <td>{{ row.category }}</td>
                <td>{{ row.system }}</td>
                <td><span class="badge complyon-badge text-xs" :class="row.sensitivity.class">{{ row.sensitivity.label }}</span></td>
                <td><span class="badge complyon-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <div v-if="pi_inventory_modal_open || active_pi_inventory_drawer" class="command-centre-drawer-backdrop" @click="pi_inventory_modal_open ? close_pi_inventory_modal() : close_pi_inventory_drawer()"></div>

      <div
        v-if="pi_inventory_modal_open"
        class="supplier-register-modal-wrap"
        role="dialog"
        aria-modal="true"
        :aria-label="pi_inventory_modal_title"
      >
        <div class="supplier-register-modal privacy-pi-modal card border-0">
          <div class="supplier-register-modal-header d-flex align-items-start justify-content-between gap-3">
            <div>
              <h2 class="supplier-register-modal-title mb-1">{{ pi_inventory_modal_title }}</h2>
              <div class="supplier-register-modal-subtitle">
                A category of personal information the organisation holds - where it lives, who it relates to and which processing activity uses it.
              </div>
            </div>
            <button
              type="button"
              class="btn btn-link text-decoration-none text-secondary p-0"
              @click="close_pi_inventory_modal"
              aria-label="Close personal information element modal"
            >
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <div class="supplier-register-modal-body">
            <div class="row g-3">
              <div class="col-12">
                <label class="form-label supplier-register-modal-label">PI element</label>
                <input v-model="pi_inventory_form.item" type="text" class="form-control supplier-register-modal-input" placeholder="e.g. Banking & payroll details" />
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label supplier-register-modal-label">Data subject</label>
                <input v-model="pi_inventory_form.subject" type="text" class="form-control supplier-register-modal-input" placeholder="e.g. Employees" />
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label supplier-register-modal-label">Category</label>
                <input v-model="pi_inventory_form.category" type="text" class="form-control supplier-register-modal-input" placeholder="e.g. Special / financial" />
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label supplier-register-modal-label">System / location</label>
                <input v-model="pi_inventory_form.system" type="text" class="form-control supplier-register-modal-input" placeholder="e.g. Payroll (Sage)" />
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label supplier-register-modal-label">Source</label>
                <input v-model="pi_inventory_form.source" type="text" class="form-control supplier-register-modal-input" placeholder="e.g. HR forms" />
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label supplier-register-modal-label">Linked processing activity</label>
                <input v-model="pi_inventory_form.linked_processing_activity" type="text" class="form-control supplier-register-modal-input" placeholder="e.g. PA-06 - Payroll" />
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label supplier-register-modal-label">Owner</label>
                <input v-model="pi_inventory_form.owner" type="text" class="form-control supplier-register-modal-input" placeholder="Responsible team" />
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label supplier-register-modal-label">Sensitivity</label>
                <select v-model="pi_inventory_form.sensitivity" class="form-select supplier-register-modal-input">
                  <option v-for="option in sensitivity_options" :key="option" :value="option">{{ option }}</option>
                </select>
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label supplier-register-modal-label">Status</label>
                <select v-model="pi_inventory_form.status" class="form-select supplier-register-modal-input">
                  <option v-for="option in pi_inventory_status_options" :key="option" :value="option">{{ option }}</option>
                </select>
              </div>
            </div>
          </div>

          <div class="supplier-register-modal-footer d-flex align-items-center justify-content-end gap-2">
            <button type="button" class="btn btn-link text-decoration-none text-secondary" @click="close_pi_inventory_modal">Cancel</button>
            <button type="button" class="btn config-navbar-primary-action d-inline-flex align-items-center gap-2" @click="submit_pi_inventory_modal">
              <i class="bi bi-plus-lg"></i>
              <span>{{ pi_inventory_modal_action_label }}</span>
            </button>
          </div>
        </div>
      </div>

      <aside
        v-if="active_pi_inventory_drawer"
        class="command-centre-drawer privacy-pi-drawer"
        role="dialog"
        aria-modal="true"
        :aria-label="active_pi_inventory_drawer.item"
      >
        <div class="command-centre-drawer-header border-bottom">
          <div class="d-flex align-items-start justify-content-between gap-3">
            <div class="min-w-0">
              <div class="command-centre-drawer-kicker text-uppercase">Personal information element - {{ active_pi_inventory_drawer.ref }}</div>
              <h2 class="command-centre-drawer-title mb-1">{{ active_pi_inventory_drawer.item }}</h2>
              <div class="command-centre-drawer-subtitle">Owner {{ active_pi_inventory_drawer.owner }}</div>
            </div>
            <div class="d-flex align-items-center gap-2 flex-shrink-0">
              <span class="badge rounded-pill complyon-badge text-xs" :class="active_pi_inventory_drawer.status.class">{{ active_pi_inventory_drawer.status.label }}</span>
              <button type="button" class="btn btn-link text-decoration-none p-0 command-centre-drawer-close" @click="close_pi_inventory_drawer" aria-label="Close drawer">
                <i class="bi bi-x-lg fs-5"></i>
              </button>
            </div>
          </div>
        </div>

        <div ref="piInventoryDrawerBody" class="command-centre-drawer-body privacy-pi-drawer-body">
          <section class="privacy-pi-drawer-details">
            <div class="privacy-pi-detail-row">
              <div class="privacy-pi-detail-label">PI element</div>
              <div class="privacy-pi-detail-value">{{ active_pi_inventory_drawer.item }}</div>
            </div>
            <div class="privacy-pi-detail-row">
              <div class="privacy-pi-detail-label">Data subject</div>
              <div class="privacy-pi-detail-value">{{ active_pi_inventory_drawer.subject }}</div>
            </div>
            <div class="privacy-pi-detail-row">
              <div class="privacy-pi-detail-label">Category</div>
              <div class="privacy-pi-detail-value">{{ active_pi_inventory_drawer.category }}</div>
            </div>
            <div class="privacy-pi-detail-row">
              <div class="privacy-pi-detail-label">System / location</div>
              <div class="privacy-pi-detail-value">{{ active_pi_inventory_drawer.system }}</div>
            </div>
            <div class="privacy-pi-detail-row">
              <div class="privacy-pi-detail-label">Source</div>
              <div class="privacy-pi-detail-value">{{ active_pi_inventory_drawer.source }}</div>
            </div>
            <div class="privacy-pi-detail-row">
              <div class="privacy-pi-detail-label">Linked processing activity</div>
              <div class="privacy-pi-detail-value">{{ active_pi_inventory_drawer.linked_processing_activity }}</div>
            </div>
            <div class="privacy-pi-detail-row">
              <div class="privacy-pi-detail-label">Owner</div>
              <div class="privacy-pi-detail-value">{{ active_pi_inventory_drawer.owner }}</div>
            </div>
            <div class="privacy-pi-detail-row">
              <div class="privacy-pi-detail-label">Sensitivity</div>
              <div class="privacy-pi-detail-value">{{ active_pi_inventory_drawer.sensitivity.label }}</div>
            </div>
            <div class="privacy-pi-detail-row">
              <div class="privacy-pi-detail-label">Status</div>
              <div class="privacy-pi-detail-value">
                <span class="badge complyon-badge text-xs" :class="active_pi_inventory_drawer.status.class">{{ active_pi_inventory_drawer.status.label }}</span>
              </div>
            </div>
          </section>

          <section v-if="active_pi_inventory_drawer.connected_records.length" class="mt-4">
            <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
              <div class="command-centre-drawer-section-title mb-0">Connected records</div>
              <div class="privacy-pi-connected-count">{{ active_pi_inventory_drawer.connected_records.length }}</div>
            </div>
            <div class="row g-2">
              <div v-for="record in active_pi_inventory_drawer.connected_records" :key="`${record.type}-${record.title}`" class="col-12 col-md-6">
                <div class="privacy-pi-linked-card">
                  <div class="d-flex align-items-start justify-content-between gap-2">
                    <div class="d-flex align-items-start gap-2 min-w-0">
                      <span class="privacy-pi-linked-icon">
                        <i :class="`bi ${record.icon || 'bi-link-45deg'}`"></i>
                      </span>
                      <div class="min-w-0">
                        <div class="privacy-pi-linked-label">{{ record.type }}</div>
                        <div class="privacy-pi-linked-title">{{ record.title }}</div>
                        <div v-if="record.subtitle" class="privacy-pi-linked-subtitle">{{ record.subtitle }}</div>
                      </div>
                    </div>
                    <button type="button" class="btn btn-link p-0 privacy-pi-linked-action" aria-label="Open linked record">
                      <i class="bi bi-box-arrow-up-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div class="command-centre-drawer-footer border-top">
          <div class="d-flex align-items-center justify-content-between gap-2 flex-wrap">
            <button type="button" class="btn privacy-pi-drawer-secondary-btn">
              <i class="bi bi-file-earmark-text"></i>
              <span>Evidence</span>
            </button>
            <button type="button" class="btn config-navbar-primary-action d-inline-flex align-items-center gap-2" @click="edit_active_pi_inventory_record">
              <i class="bi bi-gear"></i>
              <span>Edit record</span>
            </button>
          </div>
        </div>
      </aside>
    </div>

    <div v-else-if="active_tab === 'processing-register'">
      <section class="complyon-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
          <div>
            <div class="complyon-section-title text-s">Personal information processing register</div>
            <div class="complyon-section-subtitle text-xs">Every processing activity - purpose, lawful basis, owner, retention and safeguards. Open one for its connected records.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn" @click="open_processing_modal()">
            <i class="bi bi-plus-lg"></i>
            <span>New activity</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table privacy-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Ref</th>
                <th class="text-xs">Activity</th>
                <th class="text-xs">Category</th>
                <th class="text-xs">Lawful basis</th>
                <th class="text-xs">Owner</th>
                <th class="text-xs">Retention</th>
                <th class="text-xs">Status</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in processing_rows"
                :key="row.ref"
                class="privacy-processing-row"
                tabindex="0"
                role="button"
                @click="open_processing_drawer(row)"
                @keydown.enter.prevent="open_processing_drawer(row)"
                @keydown.space.prevent="open_processing_drawer(row)"
              >
                <td class="ps-3 fw-semibold text-success">{{ row.ref }}</td>
                <td class="fw-semibold privacy-name">{{ row.activity }}</td>
                <td>{{ row.category }}</td>
                <td>{{ row.basis }}</td>
                <td>{{ row.owner }}</td>
                <td>{{ row.retention }}</td>
                <td><span class="badge complyon-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <div v-if="processing_modal_open || active_processing_drawer" class="command-centre-drawer-backdrop" @click="processing_modal_open ? close_processing_modal() : close_processing_drawer()"></div>

      <div
        v-if="processing_modal_open"
        class="supplier-register-modal-wrap"
        role="dialog"
        aria-modal="true"
        :aria-label="processing_modal_title"
      >
        <div class="supplier-register-modal privacy-processing-modal card border-0">
          <div class="supplier-register-modal-header d-flex align-items-start justify-content-between gap-3">
            <div>
              <h2 class="supplier-register-modal-title mb-1">{{ processing_modal_title }}</h2>
              <div class="supplier-register-modal-subtitle">
                A record of personal-information processing - purpose, lawful basis, retention and safeguards.
              </div>
            </div>
            <button
              type="button"
              class="btn btn-link text-decoration-none text-secondary p-0"
              @click="close_processing_modal"
              aria-label="Close processing activity modal"
            >
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <div class="supplier-register-modal-body">
            <div class="row g-3">
              <div class="col-12">
                <label class="form-label supplier-register-modal-label">Activity</label>
                <input v-model="processing_form.activity" type="text" class="form-control supplier-register-modal-input" placeholder="e.g. Employee HR records" />
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label supplier-register-modal-label">Data category</label>
                <input v-model="processing_form.category" type="text" class="form-control supplier-register-modal-input" placeholder="e.g. Personal / Special" />
              </div>
              <div class="col-12">
                <label class="form-label supplier-register-modal-label">Purpose</label>
                <input v-model="processing_form.purpose" type="text" class="form-control supplier-register-modal-input" placeholder="Why it is processed" />
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label supplier-register-modal-label">Lawful basis</label>
                <select v-model="processing_form.basis" class="form-select supplier-register-modal-input">
                  <option v-for="option in processing_basis_options" :key="option" :value="option">{{ option }}</option>
                </select>
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label supplier-register-modal-label">Owner</label>
                <input v-model="processing_form.owner" type="text" class="form-control supplier-register-modal-input" placeholder="Responsible team" />
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label supplier-register-modal-label">Retention</label>
                <input v-model="processing_form.retention" type="text" class="form-control supplier-register-modal-input" placeholder="e.g. 7 years" />
              </div>
              <div class="col-12">
                <label class="form-label supplier-register-modal-label">Safeguards</label>
                <input v-model="processing_form.safeguards" type="text" class="form-control supplier-register-modal-input" placeholder="Controls protecting the data" />
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label supplier-register-modal-label">Status</label>
                <select v-model="processing_form.status" class="form-select supplier-register-modal-input">
                  <option v-for="option in processing_status_options" :key="option" :value="option">{{ option }}</option>
                </select>
              </div>
            </div>
          </div>

          <div class="supplier-register-modal-footer d-flex align-items-center justify-content-end gap-2">
            <button type="button" class="btn btn-link text-decoration-none text-secondary" @click="close_processing_modal">Cancel</button>
            <button type="button" class="btn config-navbar-primary-action d-inline-flex align-items-center gap-2" @click="submit_processing_modal">
              <i class="bi bi-plus-lg"></i>
              <span>{{ processing_modal_action_label }}</span>
            </button>
          </div>
        </div>
      </div>

      <aside
        v-if="active_processing_drawer"
        class="command-centre-drawer privacy-processing-drawer"
        role="dialog"
        aria-modal="true"
        :aria-label="active_processing_drawer.activity"
      >
        <div class="command-centre-drawer-header border-bottom">
          <div class="d-flex align-items-start justify-content-between gap-3">
            <div class="min-w-0">
              <div class="command-centre-drawer-kicker text-uppercase">Processing activity - {{ active_processing_drawer.ref }}</div>
              <h2 class="command-centre-drawer-title mb-1">{{ active_processing_drawer.activity }}</h2>
              <div class="command-centre-drawer-subtitle">Owner {{ active_processing_drawer.owner }}</div>
            </div>
            <div class="d-flex align-items-center gap-2 flex-shrink-0">
              <span class="badge rounded-pill complyon-badge text-xs" :class="active_processing_drawer.status.class">{{ active_processing_drawer.status.label }}</span>
              <button type="button" class="btn btn-link text-decoration-none p-0 command-centre-drawer-close" @click="close_processing_drawer" aria-label="Close drawer">
                <i class="bi bi-x-lg fs-5"></i>
              </button>
            </div>
          </div>
        </div>

        <div ref="processingDrawerBody" class="command-centre-drawer-body privacy-processing-drawer-body">
          <section class="privacy-processing-drawer-details">
            <div class="privacy-processing-detail-row">
              <div class="privacy-processing-detail-label">Activity</div>
              <div class="privacy-processing-detail-value">{{ active_processing_drawer.activity }}</div>
            </div>
            <div class="privacy-processing-detail-row">
              <div class="privacy-processing-detail-label">Data category</div>
              <div class="privacy-processing-detail-value">{{ active_processing_drawer.category }}</div>
            </div>
            <div class="privacy-processing-detail-row">
              <div class="privacy-processing-detail-label">Purpose</div>
              <div class="privacy-processing-detail-value">{{ active_processing_drawer.purpose }}</div>
            </div>
            <div class="privacy-processing-detail-row">
              <div class="privacy-processing-detail-label">Lawful basis</div>
              <div class="privacy-processing-detail-value">{{ active_processing_drawer.basis }}</div>
            </div>
            <div class="privacy-processing-detail-row">
              <div class="privacy-processing-detail-label">Owner</div>
              <div class="privacy-processing-detail-value">{{ active_processing_drawer.owner }}</div>
            </div>
            <div class="privacy-processing-detail-row">
              <div class="privacy-processing-detail-label">Retention</div>
              <div class="privacy-processing-detail-value">{{ active_processing_drawer.retention }}</div>
            </div>
            <div class="privacy-processing-detail-row">
              <div class="privacy-processing-detail-label">Safeguards</div>
              <div class="privacy-processing-detail-value">{{ active_processing_drawer.safeguards }}</div>
            </div>
            <div class="privacy-processing-detail-row">
              <div class="privacy-processing-detail-label">Status</div>
              <div class="privacy-processing-detail-value">
                <span class="badge complyon-badge text-xs" :class="active_processing_drawer.status.class">{{ active_processing_drawer.status.label }}</span>
              </div>
            </div>
          </section>

          <section v-if="active_processing_drawer.connected_records.length" class="mt-4">
            <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
              <div class="command-centre-drawer-section-title mb-0">Connected records</div>
              <div class="privacy-processing-connected-count">{{ active_processing_drawer.connected_records.length }}</div>
            </div>
            <div class="row g-2">
              <div v-for="record in active_processing_drawer.connected_records" :key="`${record.type}-${record.title}`" class="col-12 col-md-6">
                <div class="privacy-processing-linked-card">
                  <div class="d-flex align-items-start justify-content-between gap-2">
                    <div class="d-flex align-items-start gap-2 min-w-0">
                      <span class="privacy-processing-linked-icon">
                        <i :class="`bi ${record.icon || 'bi-link-45deg'}`"></i>
                      </span>
                      <div class="min-w-0">
                        <div class="privacy-processing-linked-label">{{ record.type }}</div>
                        <div class="privacy-processing-linked-title">{{ record.title }}</div>
                        <div v-if="record.subtitle" class="privacy-processing-linked-subtitle">{{ record.subtitle }}</div>
                      </div>
                    </div>
                    <button type="button" class="btn btn-link p-0 privacy-processing-linked-action" aria-label="Open linked record">
                      <i class="bi bi-box-arrow-up-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div class="command-centre-drawer-footer border-top">
          <div class="d-flex align-items-center justify-content-between gap-2 flex-wrap">
            <button type="button" class="btn privacy-processing-drawer-secondary-btn">
              <i class="bi bi-file-earmark-text"></i>
              <span>Evidence</span>
            </button>
            <button type="button" class="btn config-navbar-primary-action d-inline-flex align-items-center gap-2" @click="edit_active_processing_record">
              <i class="bi bi-gear"></i>
              <span>Edit record</span>
            </button>
          </div>
        </div>
      </aside>
    </div>

    <div v-else-if="active_tab === 'dsars'">
      <section class="complyon-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
          <div>
            <div class="complyon-section-title text-s">Data subject request tracker</div>
            <div class="complyon-section-subtitle text-xs">Access, correction, erasure and objection requests - 30-day statutory SLA clock.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn" @click="open_dsar_modal()">
            <i class="bi bi-plus-lg"></i>
            <span>Log request</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table privacy-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Ref</th>
                <th class="text-xs">Data subject</th>
                <th class="text-xs">Type</th>
                <th class="text-xs">Received</th>
                <th class="text-xs">Due</th>
                <th class="text-xs">SLA</th>
                <th class="text-xs">Owner</th>
                <th class="text-xs">Status</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in dsar_rows"
                :key="row.ref"
                class="privacy-dsar-row"
                tabindex="0"
                role="button"
                @click="open_dsar_modal(row, dsar_rows.indexOf(row))"
                @keydown.enter.prevent="open_dsar_modal(row, dsar_rows.indexOf(row))"
                @keydown.space.prevent="open_dsar_modal(row, dsar_rows.indexOf(row))"
              >
                <td class="ps-3 fw-semibold text-success">{{ row.ref }}</td>
                <td class="fw-semibold privacy-name">{{ row.subject }}</td>
                <td>{{ row.type }}</td>
                <td>{{ row.received }}</td>
                <td>{{ row.due }}</td>
                <td><span class="badge complyon-badge text-xs" :class="row.status.class">{{ row.sla }}</span></td>
                <td>{{ row.owner }}</td>
                <td><span class="badge complyon-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <div v-if="dsar_modal_open" class="command-centre-drawer-backdrop" @click="close_dsar_modal"></div>

      <div
        v-if="dsar_modal_open"
        class="supplier-register-modal-wrap"
        role="dialog"
        aria-modal="true"
        :aria-label="dsar_modal_title"
      >
        <div class="supplier-register-modal privacy-dsar-modal card border-0">
          <div class="supplier-register-modal-header d-flex align-items-start justify-content-between gap-3">
            <div>
              <h2 class="supplier-register-modal-title mb-1">{{ dsar_modal_title }}</h2>
              <div class="supplier-register-modal-subtitle">
                A request from a data subject - 30-day statutory response clock.
              </div>
            </div>
            <button
              type="button"
              class="btn btn-link text-decoration-none text-secondary p-0"
              @click="close_dsar_modal"
              aria-label="Close data subject request modal"
            >
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <div class="supplier-register-modal-body">
            <div class="row g-3">
              <div class="col-12">
                <label class="form-label supplier-register-modal-label">Data subject</label>
                <input v-model="dsar_form.subject" type="text" class="form-control supplier-register-modal-input" placeholder="Who made the request" />
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label supplier-register-modal-label">Request type</label>
                <select v-model="dsar_form.type" class="form-select supplier-register-modal-input">
                  <option v-for="option in dsar_type_options" :key="option" :value="option">{{ option }}</option>
                </select>
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label supplier-register-modal-label">Received</label>
                <input v-model="dsar_form.received" type="text" class="form-control supplier-register-modal-input" placeholder="e.g. 09 May 2026" />
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label supplier-register-modal-label">Due</label>
                <input v-model="dsar_form.due" type="text" class="form-control supplier-register-modal-input" placeholder="e.g. 08 Jun 2026" />
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label supplier-register-modal-label">Owner</label>
                <input v-model="dsar_form.owner" type="text" class="form-control supplier-register-modal-input" placeholder="Responsible team" />
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label supplier-register-modal-label">Status</label>
                <select v-model="dsar_form.status" class="form-select supplier-register-modal-input">
                  <option v-for="option in dsar_status_options" :key="option" :value="option">{{ option }}</option>
                </select>
              </div>
            </div>
          </div>

          <div class="supplier-register-modal-footer d-flex align-items-center justify-content-between gap-2 flex-wrap">
            <button
              v-if="dsar_edit_index !== null"
              type="button"
              class="btn btn-link text-decoration-none text-secondary d-inline-flex align-items-center gap-2"
              @click="delete_dsar_modal"
            >
              <i class="bi bi-trash"></i>
              <span>Delete</span>
            </button>
            <div class="d-flex align-items-center gap-2 ms-auto">
              <button type="button" class="btn btn-link text-decoration-none text-secondary" @click="close_dsar_modal">Cancel</button>
              <button type="button" class="btn config-navbar-primary-action d-inline-flex align-items-center gap-2" @click="submit_dsar_modal">
                <i :class="dsar_edit_index === null ? 'bi bi-plus-lg' : 'bi bi-check2-square'"></i>
                <span>{{ dsar_modal_action_label }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="active_tab === 'consent'">
      <section class="complyon-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
          <div>
            <div class="complyon-section-title text-s">Consent records</div>
            <div class="complyon-section-subtitle text-xs">Evidence that consent was captured for each marketing or special-PI purpose.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn">
            <i class="bi bi-plus-lg"></i>
            <span>New consent record</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Purpose</th>
                <th class="text-xs">Channel</th>
                <th class="text-xs">Method</th>
                <th class="text-xs">Volume</th>
                <th class="text-xs">Last audit</th>
                <th class="text-xs">Status</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in consent_rows" :key="row.purpose">
                <td class="ps-3 fw-semibold">{{ row.purpose }}</td>
                <td>{{ row.channel }}</td>
                <td>{{ row.method }}</td>
                <td>{{ row.volume }}</td>
                <td>{{ row.audit }}</td>
                <td><span class="badge complyon-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'sharing-operators'">
      <section class="complyon-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
          <div>
            <div class="complyon-section-title text-s">Data sharing register & operator agreements</div>
            <div class="complyon-section-subtitle text-xs">Third parties personal data is shared with, and whether a signed operator agreement (DPA) is in place. Open one for its connected records.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn">
            <i class="bi bi-plus-lg"></i>
            <span>New sharing record</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Recipient</th>
                <th class="text-xs">Data type</th>
                <th class="text-xs">Direction</th>
                <th class="text-xs">Operator agreement</th>
                <th class="text-xs">Status</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in sharing_rows" :key="row.recipient">
                <td class="ps-3 fw-semibold">{{ row.recipient }}</td>
                <td>{{ row.data_type }}</td>
                <td>{{ row.direction }}</td>
                <td><span class="fw-semibold">{{ row.agreement }}</span></td>
                <td><span class="badge complyon-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'pias'">
      <section class="complyon-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
          <div>
            <div class="complyon-section-title text-s">Privacy impact assessments</div>
            <div class="complyon-section-subtitle text-xs">Risk assessments for new or changed processing - required before high-risk go-lives.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn">
            <i class="bi bi-plus-lg"></i>
            <span>New PIA</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Ref</th>
                <th class="text-xs">System / processing</th>
                <th class="text-xs">Trigger</th>
                <th class="text-xs">Risk</th>
                <th class="text-xs">Owner</th>
                <th class="text-xs">Due</th>
                <th class="text-xs">Status</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in pia_rows" :key="row.ref">
                <td class="ps-3 fw-semibold text-success">{{ row.ref }}</td>
                <td class="fw-semibold">{{ row.system }}</td>
                <td>{{ row.trigger }}</td>
                <td><span class="badge complyon-badge text-xs" :class="row.risk.class">{{ row.risk.label }}</span></td>
                <td>{{ row.owner }}</td>
                <td>{{ row.due }}</td>
                <td><span class="badge complyon-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'retention'">
      <section class="complyon-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
          <div>
            <div class="complyon-section-title text-s">Retention schedule</div>
            <div class="complyon-section-subtitle text-xs">How long each record class is kept, the disposal trigger and method.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn">
            <i class="bi bi-plus-lg"></i>
            <span>New retention rule</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Record class</th>
                <th class="text-xs">Retention period</th>
                <th class="text-xs">Disposal trigger</th>
                <th class="text-xs">Method</th>
                <th class="text-xs">Status</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in retention_rows" :key="row.record_class">
                <td class="ps-3 fw-semibold">{{ row.record_class }}</td>
                <td>{{ row.retention }}</td>
                <td>{{ row.trigger }}</td>
                <td>{{ row.method }}</td>
                <td><span class="badge complyon-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'cross-border'">
      <section class="complyon-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
          <div>
            <div class="complyon-section-title text-s">Cross-border transfer register</div>
            <div class="complyon-section-subtitle text-xs">Personal data leaving the country, and the mechanism that legitimises each transfer.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn">
            <i class="bi bi-plus-lg"></i>
            <span>New transfer</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Ref</th>
                <th class="text-xs">Destination</th>
                <th class="text-xs">Data type</th>
                <th class="text-xs">Mechanism</th>
                <th class="text-xs">Reviewed</th>
                <th class="text-xs">Status</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in cross_border_rows" :key="row.ref">
                <td class="ps-3 fw-semibold text-success">{{ row.ref }}</td>
                <td class="fw-semibold">{{ row.destination }}</td>
                <td>{{ row.data_type }}</td>
                <td>{{ row.mechanism }}</td>
                <td>{{ row.reviewed }}</td>
                <td><span class="badge complyon-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'incidents'">
      <section class="complyon-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
          <div>
            <div class="complyon-section-title text-s">Privacy incident management</div>
            <div class="complyon-section-subtitle text-xs">Privacy breaches and near-misses, with Information Regulator notification status.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn">
            <i class="bi bi-plus-lg"></i>
            <span>Log incident</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Ref</th>
                <th class="text-xs">Incident</th>
                <th class="text-xs">Severity</th>
                <th class="text-xs">Reported</th>
                <th class="text-xs">Regulator</th>
                <th class="text-xs">Status</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in incident_rows" :key="row.ref">
                <td class="ps-3 fw-semibold text-success">{{ row.ref }}</td>
                <td class="fw-semibold">{{ row.incident }}</td>
                <td><span class="badge complyon-badge text-xs" :class="row.severity.class">{{ row.severity.label }}</span></td>
                <td class="fw-semibold">{{ row.reported }}</td>
                <td>{{ row.regulator }}</td>
                <td><span class="badge complyon-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else>
      <div class="text-muted small">Select a tab to view data privacy records.</div>
    </div>
  </div>
</div>
